'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card, CardContent, CardFooter } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { Progress } from '@ahafood/ui/components/progress';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { type Address, assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import { Clock, Gift, MapPin, Zap } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import DealSwitchTabs, { type DealMode } from './deal-switch-tab';
import {
  useGetFlashDeals,
  type FlashDealItem as ApiProgress,
} from '@/api/use-get-flash-deals';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { LoginDialog } from '@/app/[locale]/(main)/store/components/fb-login';
import ImageWithFallback from '@/app/[locale]/(main)/store/components/items/fallback-image';
import { ResultBlock } from '@/components/result-block';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import type { MenuItem } from '@/types/item';

type StoreDealProgressProps = {
  /** SSR prefetch cho tab ban đầu (theo deal_mode) — seed initialData. */
  data: ApiProgress[];
  payload: {
    city_id: string;
    lat?: number;
    lng?: number;
  };
};

const getTimeRange = (store_open_hours: any) => {
  if (!store_open_hours) return '06:00 - 22:00';

  const today = new Date().getDay();
  const dayKey = today === 0 ? '6' : (today - 1).toString();

  const todayHours = store_open_hours[dayKey];
  if (!todayHours || todayHours.length === 0) return 'Đóng cửa';

  const firstSlot = todayHours[0];
  const lastSlot = todayHours[todayHours.length - 1];

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  return `${formatTime(firstSlot.start_time)} - ${formatTime(lastSlot.end_time)}`;
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

const renderDealBadge = (dealItem: ApiProgress) => {
  const { discount_config } = dealItem;
  let badgeText = 'Deal';

  if (discount_config.type === 'VALUE') {
    badgeText = `Giảm ${discount_config.value.toLocaleString()}đ`;
  } else if (discount_config.type === 'FLAT') {
    badgeText = `Đồng giá ${discount_config.value.toLocaleString()}đ`;
  } else if (discount_config.type === 'PERCENT') {
    badgeText = `Giảm ${discount_config.value}%`;
  }

  return (
    <Badge className="absolute top-3 md:left-3 left-2 z-10 bg-gradient-to-r from-red-40 to-primary text-white font-bold md:px-3 px-1.5 md:py-1.5 py-0.5 text-xs shadow-lg rounded-full">
      ⚡ {badgeText}
    </Badge>
  );
};

// Optimized DealCard Component with unified responsive design
const DealCard = ({
  dealItem,
  index,
  isMobile,
  onSendMessage,
  onDealToggle,
}: {
  dealItem: ApiProgress;
  index: number;
  isMobile: boolean;
  onSendMessage: (item: ApiProgress) => void;
  onDealToggle: (item: ApiProgress, status: string) => void;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const status = dealItem.status;
  const timeRange = getTimeRange(dealItem.store_open_hours);

  // Ưu tiên daily cap (max_per_day) — nếu có cap daily, hiển thị progress
  // theo "hôm nay". Else fallback lifetime cap (max_use). Else unlimited.
  const hasDailyCap = dealItem.max_per_day !== -1;
  const hasLifetimeCap = dealItem.max_use !== -1;
  const isUnlimited = !hasDailyCap && !hasLifetimeCap;
  const capTotal = hasDailyCap ? dealItem.max_per_day : dealItem.max_use;
  const capUsed = hasDailyCap
    ? dealItem.per_day_usage_count
    : dealItem.usage_count;
  const remaining = isUnlimited ? -1 : capTotal - capUsed;
  const progressValue = isUnlimited
    ? 50
    : Math.max(5, (capUsed / capTotal) * 100);
  const dailySuffix = hasDailyCap ? ' hôm nay' : '';

  // Mobile: horizontal compact row (Grab-style list card)
  if (isMobile) {
    const isDisabled = status === 'INACTIVE' || status === 'OUT_OF_STOCK';

    return (
      <motion.div
        whileTap={{ scale: 0.985 }}
        className="cursor-pointer"
        onClick={() => onDealToggle(dealItem, dealItem.status)}
      >
        <Card
          className={cn(
            'relative overflow-hidden rounded-[24px]',
            'border border-neutral-15 bg-white',
            'shadow-[0_2px_12px_rgba(0,0,0,0.04)]',
            'transition-all duration-200',
            'active:scale-[0.99]'
          )}
        >
          <div className="flex gap-3 p-3">
            {/* IMAGE */}
            <div className="relative shrink-0">
              <div className="relative size-32 overflow-hidden rounded-2xl bg-neutral-10">
                <ImageWithFallback
                  src={
                    dealItem.item_image_url || dealItem.store_image_url || ''
                  }
                  alt={dealItem.item_name || 'Deal image'}
                  fill
                  style={{ objectFit: 'cover' }}
                  fallbackSrc={`${assetPrefix}/static/images/empty-store.webp`}
                />

                {/* overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

                {/* AI badge */}
                {dealItem.is_ai_image ? (
                  <div className="absolute top-1.5 right-1.5 z-20 flex items-center justify-center rounded-md bg-white/90 backdrop-blur p-1 shadow-sm">
                    <Image
                      className="size-2.5"
                      src={`${assetPrefix}/static/icons/white-ai.svg`}
                      alt="AI"
                    />
                  </div>
                ) : null}

                {/* extra items */}
                {dealItem.total_items && dealItem.total_items > 1 ? (
                  <div className="absolute bottom-1.5 left-1.5 z-20 flex items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-[10px] font-semibold text-primary shadow-sm backdrop-blur">
                    <Gift className="size-2.5" />+{dealItem.total_items - 1}
                  </div>
                ) : null}

                {/* disabled */}
                {isDisabled ? (
                  <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/55 backdrop-blur-[2px]">
                    <span className="rounded-full bg-white px-2 py-1 text-[10px] font-bold text-neutral-80">
                      Hết deal
                    </span>
                  </div>
                ) : null}
              </div>

              {/* floating deal badge */}
              <div className="absolute top-2 left-2 z-30">
                <div
                  className="
    rounded-full
    bg-gradient-to-r from-red-50 to-primary-50
    px-2.5 py-1
    shadow-md
    backdrop-blur
  "
                >
                  <div className="flex items-center gap-0 text-[10px] font-semibold text-white">
                    {/* <Zap className="size-3 fill-white" /> */}
                    {dealItem.discount_config.type === 'VALUE'
                      ? `Giảm ${dealItem.discount_config.value.toLocaleString()}đ`
                      : dealItem.discount_config.type === 'FLAT'
                        ? `Đồng giá ${dealItem.discount_config.value.toLocaleString()}đ`
                        : `Giảm ${dealItem.discount_config.value}%`}
                  </div>
                </div>
              </div>
            </div>

            {/* CONTENT */}
            <div className="flex min-w-0 flex-1 flex-col">
              {/* title */}
              <h3 className="line-clamp-1 text-[17px] font-extrabold tracking-[-0.02em] text-neutral-90">
                {dealItem.item_name}
              </h3>

              {/* location */}
              <div className="mt-1 flex items-center gap-1 text-[12px] text-neutral-50">
                <MapPin className="size-3 text-primary shrink-0" />

                <span className="truncate">
                  {dealItem.distance && dealItem.distance > 0 ? (
                    <span className="font-semibold text-primary">
                      {(dealItem.distance / 1000).toFixed(1)}km
                    </span>
                  ) : null}

                  {dealItem.distance && dealItem.distance > 0 ? ' · ' : null}

                  {dealItem.store_name}
                </span>
              </div>

              {/* PRICE */}
              <div className="mt-2 flex items-end gap-2">
                <span className="text-[22px] leading-none font-extrabold tracking-[-0.02em] text-primary">
                  {(
                    dealItem.discounted_price ?? dealItem.price
                  ).toLocaleString()}
                  đ
                </span>

                {dealItem.discounted_price ? (
                  <span className="mb-1 text-[13px] font-medium text-neutral-40 line-through">
                    {dealItem.price.toLocaleString()}đ
                  </span>
                ) : null}
              </div>

              {/* spacer */}
              <div className="flex-1" />

              {/* progress */}
              <div className="mt-3">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-primary-50">
                    {isUnlimited
                      ? 'Đang hot 🔥'
                      : `Còn ${remaining}/${capTotal}${dailySuffix}`}
                  </span>

                  {!isDisabled ? (
                    <span className="text-[11px] font-medium text-neutral-40">
                      Sắp hết
                    </span>
                  ) : null}
                </div>

                <div className="relative">
                  <Progress
                    value={progressValue}
                    className="h-[6px] rounded-full bg-neutral-15"
                  />

                  <div
                    className="absolute top-1/2 size-2.5 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_0_3px_white]"
                    style={{
                      left: `calc(${progressValue}% - 6px)`,
                    }}
                  />
                </div>
              </div>

              {/* CTA */}
              <Button
                type="button"
                className={cn(
                  'mt-3 h-10 rounded-xl text-sm font-bold',
                  'bg-gradient-to-r from-primary to-primary-50',
                  'shadow-md shadow-primary/20',
                  'active:scale-[0.98]'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  void onSendMessage(dealItem);
                }}
              >
                {isDisabled ? (
                  <>
                    <MapPin className="mr-1 size-4" />
                    Xem cửa hàng
                  </>
                ) : (
                  <>
                    <Zap className="mr-1 size-4" />
                    Đặt ngay
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    );
  }

  // Desktop: original vertical card
  return (
    <motion.div
      custom={index}
      initial={index < 8 ? 'hidden' : false}
      animate={index < 8 ? 'visible' : false}
      variants={index < 8 ? cardVariants : undefined}
      whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
      whileTap={{ scale: 0.97 }}
      className="group cursor-pointer h-full"
      onClick={() => onDealToggle(dealItem, dealItem.status)}
    >
      <Card
        className={cn(
          'overflow-hidden h-full border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white rounded-2xl',
          'flex flex-col'
        )}
      >
        <div
          className={cn(
            'relative overflow-hidden bg-gradient-to-br from-neutral-10 to-neutral-20',
            'h-48 flex-none'
          )}
        >
          {dealItem.is_ai_image && (
            <Badge className="z-50 size-7 absolute top-2 right-2 bg-white/50 hover:bg-white/90 text-white border-none shadow-sm p-1 text-[10px] font-bold">
              <Image
                fill
                className="p-1"
                src={`${assetPrefix}/static/icons/white-ai.svg`}
                alt={''}
              />
            </Badge>
          )}
          {renderDealBadge(dealItem)}

          {dealItem.total_items && dealItem.total_items > 1 && (
            <Badge
              className={cn(
                'absolute z-10 bg-yellow-10 hover:bg-yellow-10 backdrop-blur-sm text-primary-60 font-semibold shadow-md rounded-full border border-primary-30',
                isMobile
                  ? 'top-10 left-1 px-1 py-0.5 text-xs'
                  : 'top-3 right-3 px-2 py-1 text-xs'
              )}
            >
              <Gift
                className={cn(isMobile ? 'size-2.5 mr-0.5' : 'size-3 mr-1')}
              />
              +{dealItem.total_items - 1}
              {!isMobile && ' món'}
            </Badge>
          )}

          <ImageWithFallback
            src={dealItem.item_image_url || dealItem.store_image_url || ''}
            alt={dealItem.item_name || 'Deal image'}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            fallbackSrc={`${assetPrefix}/static/images/empty-store.webp`}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />

          {(status === 'INACTIVE' || status === 'OUT_OF_STOCK') && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
              <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg">
                <p className="text-neutral-70 font-bold text-sm">
                  {isMobile ? 'Hết deal' : 'Đã hết deal'}
                </p>
              </div>
            </div>
          )}
        </div>

        <CardContent
          className={cn(
            'flex flex-col justify-between',
            isMobile ? 'p-2.5 flex-1 min-w-0' : 'p-4 flex-1'
          )}
        >
          <div className={cn(isMobile ? 'mb-2' : 'mb-3')}>
            <h2
              className={cn(
                'font-bold text-neutral-90 truncate',
                isMobile ? 'text-sm mb-1' : 'text-lg mb-2'
              )}
            >
              {dealItem.item_name}
            </h2>

            <div
              className={cn(
                'flex items-center text-neutral-60',
                isMobile ? 'text-xs mb-1' : 'text-sm mb-2'
              )}
            >
              <MapPin
                className={cn(
                  'flex-none text-primary-50',
                  isMobile ? 'size-3 mr-1' : 'size-4 mr-1.5'
                )}
              />
              <span className="truncate font-medium">
                {dealItem.distance && dealItem.distance > 0 && (
                  <span className="text-primary-50">{`${(dealItem.distance / 1000).toFixed(1)}km`}</span>
                )}{' '}
                {dealItem.store_name}
              </span>
            </div>

            <div
              className={cn(
                'flex items-center text-neutral-50',
                isMobile ? 'text-xs' : 'text-sm'
              )}
            >
              <Clock
                className={cn(isMobile ? 'size-3 mr-1' : 'size-4 mr-1.5')}
              />
              <span className={!isMobile ? 'font-medium' : ''}>
                {timeRange}
              </span>
            </div>
          </div>

          {/* Progress and Action Section */}
          <div className={cn(isMobile ? 'space-y-2' : 'space-y-3')}>
            <div className="relative">
              <div className={cn('relative', isMobile ? 'mb-1' : 'mb-2')}>
                <Progress
                  value={progressValue}
                  className={cn(isMobile ? 'h-1.5' : 'h-3', 'bg-neutral-20')}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 z-10 transition-all duration-300 ease-out"
                  style={{ left: `calc(${progressValue}% - 6px)` }}
                >
                  <Zap className="size-3 fill-yellow-20 text-yellow-30" />
                </div>
              </div>
            </div>

            <div
              className={cn(
                'flex items-center justify-between',
                isMobile ? 'text-xs' : 'text-sm'
              )}
            >
              <span
                className={cn(
                  'font-semibold text-primary-50',
                  !isMobile && 'text-base'
                )}
              >
                {isUnlimited
                  ? isMobile
                    ? 'Hot 🔥'
                    : 'Đang hot 🔥'
                  : isMobile
                    ? `${remaining}/${capTotal}`
                    : `Còn ${remaining}/${capTotal} đơn${dailySuffix}`}
              </span>
            </div>

            {dealItem.discounted_price ? (
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'text-primary font-bold',
                    isMobile ? 'text-sm' : 'text-xl'
                  )}
                >
                  {dealItem.discounted_price.toLocaleString()}đ
                </span>
                <span
                  className={cn(
                    'line-through text-neutral-40 font-semibold',
                    isMobile ? 'text-xs' : 'text-sm'
                  )}
                >
                  {dealItem.price.toLocaleString()}đ
                </span>
              </div>
            ) : (
              <div
                className={cn('font-bold', isMobile ? 'text-sm' : 'text-lg')}
              >
                {dealItem.price.toLocaleString()}đ
              </div>
            )}
          </div>
          {isMobile && (
            <div className="flex items-end pt-2">
              <Button
                type="button"
                className={cn(
                  'w-full font-bold h-8 text-xs transition-all duration-300 rounded-lg',
                  'bg-gradient-to-r from-primary to-red-40 hover:from-primary-60 hover:to-red-60 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  void onSendMessage(dealItem);
                }}
              >
                {status === 'OUT_OF_STOCK' || status === 'INACTIVE' ? (
                  <>
                    <MapPin className="size-3 mr-1" />
                    Xem cửa hàng
                  </>
                ) : (
                  <>
                    <Zap className="size-3 mr-1" />
                    Đặt ngay
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>

        {!isMobile && (
          <CardFooter className="px-4 pb-4 pt-0">
            <Button
              type="button"
              className={cn(
                'w-full font-bold h-12 text-sm transition-all duration-300 rounded-xl',
                'bg-gradient-to-r from-primary to-red-40 hover:from-primary-60 hover:to-red-60 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
              )}
              onClick={(e) => {
                e.stopPropagation();
                void onSendMessage(dealItem);
              }}
            >
              {status === 'OUT_OF_STOCK' || status === 'INACTIVE' ? (
                <>
                  <MapPin className="size-5 mr-2" />
                  Xem cửa hàng
                </>
              ) : (
                <>
                  <Zap className="size-5 mr-2" />
                  Đặt ngay
                </>
              )}
            </Button>
          </CardFooter>
        )}
      </Card>
    </motion.div>
  );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function StoreDealProgress({
  data,
  payload,
}: StoreDealProgressProps) {
  // Không còn skip/infinite scroll — call 1 lần top-N sau rank.
  const DEAL_LIMIT = 200;
  const routes = useCityRoutes();
  const { isAuthenticated } = useAuthStore();
  const address = useAiFoodOrderStore.use.address() as Address | null;
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const searchParams = useSearchParams();
  const initialMode = searchParams.get('deal_mode') as DealMode;
  const initialTab: DealMode =
    initialMode === 'exclusive' || initialMode === 'today'
      ? initialMode
      : 'today';
  const [dealMode, setDealMode] = useState<DealMode>(initialTab);
  const goToOrderWebstore = useGoToOrderWebstore();
  const router = useRouter();
  const isMobile = useIsMobile();

  // lat/lng: ưu tiên address store (client), fallback payload (SSR cookie).
  const lat = address?.lat ?? payload.lat;
  const lng = address?.lng ?? payload.lng;

  const { data: fetchedDeals, isLoading } = useGetFlashDeals(
    {
      city_id: payload.city_id,
      limit: DEAL_LIMIT,
      is_scheme: dealMode === 'exclusive',
      ...(lat && lng ? { lat, lng } : {}),
    },
    {
      // Chỉ seed SSR data cho đúng tab đã prefetch.
      initialData: dealMode === initialTab && data?.length ? data : undefined,
      // Đổi tab = observe query key khác → refetch nếu data đã stale.
      // staleTime 1 phút (realtime) → trong 1 phút dùng cache, quá thì gọi lại.
      refetchOnMount: true,
    }
  );

  const deals = useMemo(() => {
    return (fetchedDeals ?? []).filter(
      (dealItem) => dealItem?.is_available !== false
    );
  }, [fetchedDeals]);

  const convertToMenuItem = useCallback((dealItem: ApiProgress): MenuItem => {
    return {
      _id: dealItem.item_id,
      name: dealItem.item_name,
      price: dealItem.price,
      discounted_price: dealItem.discounted_price,
      image_thumb_url: dealItem.item_image_url || '',
      is_ai_image: dealItem.is_ai_image ?? false,
    };
  }, []);

  const handleDealToggle = (dealItem: ApiProgress, status: string) => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'deal_card_click',
      button_location: 'flash_deal_zone',
    });
    if (status) {
      router.push(routes.storeDetail(dealItem.store_slug));
    }
  };

  const handleSendMessageWithFallback = useCallback(
    async (dealItem: ApiProgress) => {
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'deal_add_to_cart',
        button_location: 'flash_deal_zone',
      });
      const status = dealItem.status;
      if (status === 'OUT_OF_STOCK' || status === 'INACTIVE') {
        return;
      }
      if (!isAuthenticated) {
        setShowLoginDialog(true);
        return;
      }

      await goToOrderWebstore(
        dealItem.page_id.toString(),
        dealItem.store_slug,
        dealItem.nearest_branch?.id ?? '',
        convertToMenuItem(dealItem),
        dealItem.store_id.toString()
      );
      router.push(routes.storeDetail(dealItem.store_slug));
    },
    [isAuthenticated, goToOrderWebstore, convertToMenuItem, router, routes]
  );

  return (
    <>
      <DealSwitchTabs activeMode={dealMode} onModeChange={setDealMode} />
      <h1 className="text-sm md:text-xl font-semibold md:font-bold text-neutral-70 pb-6">
        Danh sách các món hot deal
      </h1>
      {isLoading ? (
        <LoadingSkeleton />
      ) : !deals || deals.length === 0 ? (
        <div className="min-h-[50svh] flex items-center justify-center">
          <ResultBlock
            className="min-h-[50svh]"
            iconUrl={`${assetPrefix}/static/images/store-empty.webp`}
            title={
              dealMode === 'exclusive'
                ? 'Chưa có deal độc quyền'
                : 'Chưa có deal hôm nay'
            }
            description="Hiện tại không có deals nào đang diễn ra tại khu vực của bạn"
          />
        </div>
      ) : (
        <div
          className={cn(
            'grid auto-rows-max items-stretch justify-items-stretch pb-20 shadow-none',
            isMobile
              ? 'gap-4 grid-cols-1'
              : 'gap-6 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 px-2 xl:px-0'
          )}
        >
          {deals.map((dealItem, index) => (
            <DealCard
              key={`deal-${dealItem.item_id}-${dealItem.store_id}-${index}`}
              dealItem={dealItem}
              index={index}
              isMobile={isMobile}
              onSendMessage={handleSendMessageWithFallback}
              onDealToggle={handleDealToggle}
            />
          ))}
        </div>
      )}

      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="deal_progress"
      />
    </>
  );
}

// Skeleton loader for infinite scroll
function LoadingSkeleton() {
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        'col-span-full grid auto-rows-max items-stretch justify-items-stretch',
        isMobile
          ? 'gap-4 grid-cols-1'
          : 'gap-6 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 px-2 xl:px-0'
      )}
    >
      {[...Array(4)].map((_, index) => (
        <Skeleton
          key={index}
          className={cn(
            'rounded-2xl bg-neutral-10 animate-pulse',
            isMobile ? 'h-36 w-full' : 'h-48 w-full'
          )}
        />
      ))}
    </div>
  );
}
